import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Row, Col, Steps } from 'antd';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../../services/authService';
import './Auth.css';

const { Title, Text } = Typography;
const { Step } = Steps;

const RegisterClinic = () => {
    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState(0);
    const [formData, setFormData] = useState({});
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const steps = [
        'Clinic Information',
        'Contact Details',
        'Admin Account'
    ];

    const onFinish = async (values) => {
        if (current < steps.length - 1) {
            setFormData({ ...formData, ...values });
            setCurrent(current + 1);
            form.resetFields();
        } else {
            const finalData = { ...formData, ...values };
            setLoading(true);

            try {
                const response = await authService.registerClinic(finalData);
                if (response.success) {
                    message.success('Clinic registered successfully! Please login.');
                    navigate('/login');
                }
            } catch (error) {
                message.error(error.response?.data?.message || 'Registration failed');
            } finally {
                setLoading(false);
            }
        }
    };

    const renderFormStep = () => {
        switch (current) {
            case 0:
                return (
                    <>
                        <Form.Item
                            name="clinicName"
                            label="Clinic Name"
                            rules={[{ required: true, message: 'Please input clinic name!' }]}
                        >
                            <Input placeholder="Enter clinic name" />
                        </Form.Item>
                        <Form.Item
                            name="ownerName"
                            label="Owner Name"
                            rules={[{ required: true, message: 'Please input owner name!' }]}
                        >
                            <Input placeholder="Enter owner name" />
                        </Form.Item>
                        <Form.Item
                            name="website"
                            label="Website (optional)"
                        >
                            <Input placeholder="https://example.com" />
                        </Form.Item>
                    </>
                );
            case 1:
                return (
                    <>
                        <Form.Item
                            name="phone"
                            label="Phone Number"
                            rules={[{ required: true, message: 'Please input phone number!' }]}
                        >
                            <Input placeholder="+1-555-0000" />
                        </Form.Item>
                        <Form.Item name={['address', 'street']} label="Street Address">
                            <Input placeholder="123 Main Street" />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name={['address', 'city']} label="City">
                                    <Input placeholder="City" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name={['address', 'state']} label="State">
                                    <Input placeholder="State" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name={['address', 'zipCode']} label="Zip Code">
                                    <Input placeholder="12345" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name={['address', 'country']} label="Country">
                                    <Input placeholder="Country" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                );
            case 2:
                return (
                    <>
                        <Form.Item
                            name="email"
                            label="Admin Email"
                            rules={[
                                { required: true, message: 'Please input email!' },
                                { type: 'email', message: 'Please enter valid email!' }
                            ]}
                        >
                            <Input placeholder="admin@clinic.com" />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[
                                { required: true, message: 'Please input password!' },
                                { min: 6, message: 'Password must be at least 6 characters!' }
                            ]}
                        >
                            <Input.Password placeholder="Enter password" />
                        </Form.Item>
                        <Form.Item
                            name="confirmPassword"
                            label="Confirm Password"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: 'Please confirm password!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Passwords do not match!'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password placeholder="Confirm password" />
                        </Form.Item>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="auth-container">
            <Card className="auth-card auth-card-register">
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <Title level={2}>Register Your Clinic</Title>
                    <Text type="secondary">Start your 14-day free trial</Text>
                </div>

                <Steps current={current} style={{ marginBottom: 32 }}>
                    {steps.map(item => (
                        <Step key={item} title={item} />
                    ))}
                </Steps>

                <Form
                    form={form}
                    name="register"
                    onFinish={onFinish}
                    layout="vertical"
                    initialValues={formData}
                >
                    {renderFormStep()}

                    <Form.Item>
                        <Row gutter={8}>
                            {current > 0 && (
                                <Col span={12}>
                                    <Button block onClick={() => setCurrent(current - 1)}>
                                        Previous
                                    </Button>
                                </Col>
                            )}
                            <Col span={current > 0 ? 12 : 24}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={loading}
                                >
                                    {current === steps.length - 1 ? 'Register' : 'Next'}
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>

                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Text>Already have an account? </Text>
                        <Link to="/login">Login here</Link>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default RegisterClinic;
